import { PATHS } from "router/config/paths";
import type { ExtractionOutcome, GuidanceBlockedReason, GuidanceStep } from "./guidanceReport";

/**
 * The Tech Pack create guide as authored (REF-6217 T1.2): its identity, the order of the steps a
 * user walks through, and the copy key for every state the feature can report.
 *
 * **Deliberately inert.** There is no predicate, gate, cursor or "what comes next" rule in this
 * file. The wizard's rules live in `utils/wizardGates.ts` and where the user actually *is* comes
 * from `utils/guidanceReport.ts`; this module only names things and points at strings. Every value
 * below could be rewritten without changing whether the app lets anyone advance — which is what
 * makes it safe to hand to whoever owns the copy.
 *
 * **Colocated on purpose.** There is no workflow registry: the coordinator (T1.3) imports this
 * module directly, and Phase 2's backend catalogue is generated *from* definitions like it. The
 * feature that owns the flow owns its description.
 *
 * Copy keys are namespace-qualified (`tech-pack:…`) because the renderer lives in the chatbot panel,
 * nowhere near this feature's default namespace — same convention as the repo's other authored
 * `labelKey` tables (e.g. `components/TrendsGrid/constants.ts`).
 */

/**
 * The flow's stable identity. Exported on its own so the places that *match* on it — T1.5's
 * `?guide=` parameter, the coordinator's active-flow record — compare against a literal type rather
 * than `string`, where a typo would be a comparison that silently never matches.
 */
export const TECHPACK_CREATE_FLOW_ID = "techpack_create";

/**
 * States the application enters on its own. They are reported steps, but not steps the user
 * navigates: `extracting` is a wait and `done` is terminal, so neither belongs in `stepOrder`.
 */
export type GuideAppState = "extracting" | "done";

/**
 * The steps the user navigates — derived from the report's own union, never restated. A step renamed
 * or added in `GuidanceStep` lands here automatically and then fails to compile in `steps` below
 * until it has been authored, which is the whole point.
 */
export type GuidedStep = Exclude<GuidanceStep, GuideAppState>;

export interface GuidedCopy {
  /** Short label for the state — matches what the app's own tab bar calls it. */
  nameKey: string;
  /** One sentence: what to do here, or what is happening. */
  instructionKey: string;
}

export interface GuidedStepCopy extends GuidedCopy {
  /** The flow completes without this step — the app gates nothing on it. Authored, not inferred. */
  optional: boolean;
}

export interface GuidedFlowDefinition {
  readonly id: string;
  /**
   * Bump when the *shape* of the flow changes in a way that makes an older reader wrong: a step
   * added, removed or reordered, or a step id whose meaning changed. A rehydrated active-flow record
   * (T1.3's sessionStorage) and Phase 2's generated catalogue both carry the version and fall back
   * to plain navigation when it is unknown. Copy edits do not bump it — nothing keys off the strings.
   */
  readonly version: number;
  /** Joins to the `feature` on the published guidance envelope, and to the chatbot's capability id. */
  readonly capabilityId: string;
  readonly entry: string;
  readonly titleKey: string;
  /** Shown when the report is absent — the user is off the flow's page and can come back. */
  readonly awayKey: string;
  readonly stepOrder: readonly GuidedStep[];
  readonly steps: Readonly<Record<GuidedStep, GuidedStepCopy>>;
  readonly appStates: Readonly<Record<GuideAppState, GuidedCopy>>;
  /** Copy for a step the app is blocking, keyed by the reason the feature reported. */
  readonly blockedKeys: Readonly<Record<GuidanceBlockedReason, string>>;
  /** Copy for how the last extraction attempt ended. Points at the app's own Create button. */
  readonly noticeKeys: Readonly<Record<ExtractionOutcome, string>>;
}

export const TECHPACK_CREATE_FLOW: GuidedFlowDefinition = {
  id: TECHPACK_CREATE_FLOW_ID,
  // 2: the Front / Back / Side steps became one Images step (D-STR-techpack-image-intake, 2026-10-15).
  version: 2,
  capabilityId: "tech_pack",
  // The create route with its optional project id dropped — `PATHS.TECH_PACK` is a route pattern
  // (`/tech-pack/:id?`), not a URL, and the same `.replace()` is how the rest of the app navigates
  // to a fresh tech pack (`TechPackAssets.tsx`). A guided run starts a new project, so no id and no
  // query params ride along here.
  entry: PATHS.TECH_PACK.replace("/:id?", ""),
  titleKey: "tech-pack:guide.title",
  awayKey: "tech-pack:guide.away",
  // The navigable steps, in the order the wizard presents them. `extracting` and `done` are absent by construction —
  // they live in `appStates`.
  stepOrder: ["images", "review"],
  steps: {
    images: {
      nameKey: "tech-pack:guide.steps.images.name",
      instructionKey: "tech-pack:guide.steps.images.instruction",
      optional: false,
    },
    review: {
      // Semantic id `review`, but the copy says "Create" — that is what the tab the user is looking at is called.
      nameKey: "tech-pack:guide.steps.review.name",
      instructionKey: "tech-pack:guide.steps.review.instruction",
      optional: false,
    },
  },
  appStates: {
    extracting: {
      nameKey: "tech-pack:guide.states.extracting.name",
      instructionKey: "tech-pack:guide.states.extracting.instruction",
    },
    done: {
      nameKey: "tech-pack:guide.states.done.name",
      instructionKey: "tech-pack:guide.states.done.instruction",
    },
  },
  blockedKeys: {
    images_missing: "tech-pack:guide.blocked.images_missing",
    images_analyzing: "tech-pack:guide.blocked.images_analyzing",
    images_failed: "tech-pack:guide.blocked.images_failed",
    images_incomplete: "tech-pack:guide.blocked.images_incomplete",
    images_unconfirmed: "tech-pack:guide.blocked.images_unconfirmed",
  },
  noticeKeys: {
    failed: "tech-pack:guide.notice.failed",
    timeout: "tech-pack:guide.notice.timeout",
  },
};
